import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
  DoDoNot,
  HazardTable,
  Callout,
} from './DocumentPage';

const WeldingDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Welding, Plasma Cutting & Oxy-Gas – RA & SSOW"
    documentHeading="Risk Assessment 05 & Safe System of Work: Welding, Plasma Cutting and Oxy/Gas Equipment"
    meta={[
      ['Document Reference', 'PVS-RAMS-WELD-001'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Area / Location', 'Workshop'],
      ['Activity', 'Welding, plasma cutting and oxy/gas equipment'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Welding, Plasma Cutting & Oxy-Gas – RA & SSOW',
      documentReference: 'PVS-RAMS-WELD-001',
      processActivity: 'Welding, Plasma Cutting and Oxy/Gas Equipment',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Introduction">
      <Paragraph>
        Welding equipment is used in various activities. Hazards arise from: misuse of welding gear
        and using the wrong equipment for the job; direct contact with heat generated; fires caused
        by the ignition of flammable material either nearby or on the workpiece (often started by
        sparks or drips of molten metal); the storage of gas bottles (especially in the event of a
        fire); hot work on or near containers / pipework that contain or previously contained
        combustible material; electrical shock; and harmful fumes and gases generated during hot
        work, including those from primer and paint layers and other surface treatments such as
        underseal and galvanized coatings. Exposure to all welding fume, including mild steel
        welding fume, could cause lung cancer.
      </Paragraph>
    </Section>

    <Section number="2" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard Category', 'Hazards', 'Control Measures Already in Place', 'Additional Control Measures Required']}
        rows={[
          [
            'Welding Equipment – General Hazards',
            [
              'Inadequate maintenance or misuse of the equipment resulting in fire, burns or explosion.',
              'Eye injuries from arc eye, weld slag entering the eye, weld flashes etc.',
              'Contact with hot work pieces, hot flames, sparks, hot metal splatter resulting in burns.',
              'Inhalation of harmful fumes and gases when welding, flame cutting etc. (welding fume, including mild steel welding fume, could cause lung cancer).',
              'Electrocution / burns / fire from faulty electrical welders.',
            ],
            [
              'Staff have been provided with sufficient information, instruction and training.',
              'Welding equipment is maintained at regular intervals.',
              'PPE is provided and worn: flame resistant overalls or flame resistant apron, gauntlets, welding goggles or welding helmet and safety footwear.',
              'Cylinders are kept secure and upright on a mobile stand. Cylinders are clearly marked with their contents. Cylinders are changed away from sources of ignition / in well ventilated areas.',
            ],
            'Local exhaust ventilation (LEV) should be used to remove harmful fumes away from the operator (and others).',
          ],
          [
            'Oxy-Fuel Gas Equipment – General Hazards',
            [
              'Inadequate maintenance or misuse of the equipment resulting in fire, burns or explosion.',
              'Eye injuries from arc eye, weld slag entering the eye, weld flashes etc.',
              'Contact with hot work pieces, hot flames, sparks, hot metal splatter resulting in burns.',
              'Inhalation of harmful fumes and gases when welding, flame cutting etc.',
              'Electrocution / burns / fire from faulty electrical welders.',
            ],
            [
              'Staff have been provided with sufficient information, instruction and training, including correct lighting-up and working procedures.',
              'Flash back arrestors are fitted on both the fuel and oxygen supply.',
              'PPE is provided and worn e.g. flame resistant overalls or apron, gauntlets, gas welding goggles or helmet and safety footwear.',
              'Cylinders are kept secure and upright on a mobile stand. Cylinders are clearly marked with their contents. Cylinders are changed away from sources of ignition / in well ventilated areas.',
            ],
            'Oxy-Fuel gas equipment should be inspected (CP7 inspection) at an annual interval.',
          ],
        ]}
      />
    </Section>

    <Section number="3" title="Safe System of Work – Arc Welding">
      <Paragraph className="mb-4">
        Severe and sometimes fatal electric shocks happen at electric welding apparatus, whether
        single or three-phase.
      </Paragraph>
      <DoDoNot
        doItems={[
          'Earth the workpiece. A robust flexible cable, terminating in a clamp connected to the workpiece and with its other end attached to the metalwork or earth terminal of the power source, is an efficient means of earthing.',
          'During MIG or similar welding, prevent contact between the electrode wire and any earthed metalwork to avoid heavy welding current flowing through the earth continuity conductor and destroying it. Use a safe design such as an insulated spool in an insulated chamber in the power source with the wire being fed, through insulated rollers and a tube inside the welding cable, to the torch.',
          'Use an insulated box or hook to rest the electrode holder, not the face shield, clothing or rags.',
          'Maintain the electrode holder, welding current return cables, clamps and safety earths in good condition.',
          'Weld in well ventilated areas and use the local exhaust ventilation (LEV) plant.',
          'Use respiratory protective equipment (RPE) when welding outdoors or where LEV does not control exposure.',
          'Ensure the workpiece is earthed and correctly secured before welding.',
          'Handle the equipment with care.',
          'Unplug and switch off the equipment when not in use.',
          'Avoid unintentional contact with the workpiece.',
          'Check the working area is clear of unnecessary combustible items and personnel.',
          'Check that body cavities next to welding are not filled with plastic foam which may be easily ignited. Remove this where necessary and use a heat shield to protect adjacent areas.',
          'On vehicles remove adjacent flammable trim and upholstery before welding, especially where molten metal or sparks may fall into them. Check that fuel lines and tanks will not be affected; empty and remove any which are near or shield them.',
          'Keep trailing cables to a minimum.',
          'Be aware of where the nearest fire extinguisher is located while using the equipment.',
          'When finished, check the surrounding area for evidence of fire e.g. smouldering parts.',
          'Place any parts in a safe place where there is no danger of any other person touching them.',
          'Store gas cylinders in a vertical position and ensure they are correctly secured.',
          'Store cylinders away from direct heat.',
          'Keep the number of gas cylinders stored to a minimum.',
          'Secure unused cylinders upright to a wall or other substantial structure.',
          'Transport cylinders on suitable trolleys to avoid unnecessary manual handling.',
          'Change cylinders in well ventilated areas away from sources of ignition.',
          'Unplug the welder from the mains power supply before performing maintenance or service.',
          'Keep work area clean and tidy.',
          'Keep your head away from welding fumes.',
        ]}
        doNotItems={[
          'Operate the equipment unless you are wearing the correct PPE.',
          'Use cracked or defective helmets or shields.',
          'Operate any defective equipment.',
          'Misuse the equipment.',
          'Operate the equipment just wearing a cotton shirt or jumper (flame retardant apron and gauntlets should be worn etc.).',
          'Pull or carry the equipment by its power supply lead.',
          'Pull power plugs from sockets by the power cable.',
          'Touch the plug with wet hands.',
          'Use damaged or worn cables, plugs or connections.',
          'Use the welder in wet or damp conditions.',
          'Use any unapproved torches or other components with the welder.',
          'Touch any live metal parts of the torch or electrode while the equipment is switched on.',
          'Touch the torch or workpiece immediately after welding as they will be very hot.',
          'Store or leave gas cylinders in a horizontal position.',
          'Remove labels from a cylinder or lift a cylinder by the cap guard or valve.',
          'Weld near solvents etc.',
          'Bend, strain or stand on cables or leads.',
          'Stand the welder on a metal workbench, car bodywork or similar object.',
        ]}
      />
    </Section>

    <Section number="4" title="Welding Fume – HSE WL3 Control Guidance">
      <div className="overflow-x-auto rounded-lg shadow-md">
        <table className="min-w-full text-pvs-text-muted">
          <thead>
            <tr className="bg-pvs-card-light">
              <th className="py-3 px-4 uppercase font-semibold text-sm text-left">Frequency / Duration</th>
              <th className="py-3 px-4 uppercase font-semibold text-sm text-left">Type of Welding</th>
              <th className="py-3 px-4 uppercase font-semibold text-sm text-left">Good Control Practice</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-3 px-4 align-top">Sporadic or occasional low intensity welding (&lt;1 hour per welder per shift)</td>
              <td className="py-3 px-4 align-top">Gas, MMA, FCA, MIG, MAG</td>
              <td className="py-3 px-4 align-top">LEV where reasonably practicable. Otherwise good general ventilation and RPE.</td>
            </tr>
            <tr className="bg-white/5">
              <td className="py-3 px-4 align-top">Regular high-intensity welding (&gt;1 hour arc time per welder per shift)</td>
              <td className="py-3 px-4 align-top">Gas, MMA, FCA, MIG, MAG</td>
              <td className="py-3 px-4 align-top">LEV and consider supplementary RPE.</td>
            </tr>
            <tr>
              <td className="py-3 px-4 align-top">Regular high-intensity welding outdoors in the open air</td>
              <td className="py-3 px-4 align-top">Gas, MMA, FCA, MIG, MAG, TIG</td>
              <td className="py-3 px-4 align-top">RPE where LEV is not reasonably practicable.</td>
            </tr>
            <tr className="bg-white/5">
              <td className="py-3 px-4 align-top">Sporadic or occasional low intensity welding</td>
              <td className="py-3 px-4 align-top">TIG and resistance spot welding</td>
              <td className="py-3 px-4 align-top">General ventilation.</td>
            </tr>
            <tr>
              <td className="py-3 px-4 align-top">Regular high-intensity welding</td>
              <td className="py-3 px-4 align-top">TIG and resistance spot welding</td>
              <td className="py-3 px-4 align-top">LEV.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <Paragraph className="mt-4">
        The HSE define sporadic welding as "occasional welding carried out less than once per week
        which is incidental to the business's core activity and cannot be planned for, e.g. repair
        or maintenance work".
      </Paragraph>
    </Section>

    <Section number="5" title="Special Hazards">
      <SubSection title="Explosion when Welding or Repairing Tanks, Drums or Containers">
        <Paragraph>
          If a welding electrode is used on a tank or drum containing flammable material (solid,
          liquid or vapour), it can explode. Such explosions have killed people. As well as flammable
          liquids such as petrol, diesel and fuel oil, substances such as paints, glue, anti-freeze
          and cleaning agents may also release flammable vapours. Tanks and drums that are 'empty'
          usually still have residues in the bottom, and in seams and crevices. Just a teaspoon of
          flammable liquid in a drum can be enough to cause an explosion.
        </Paragraph>
        <Callout tone="danger">
          <strong>Do Not:</strong>
          <BulletList
            className="mt-2"
            items={[
              'Use welding equipment on a drum, container or tank that has contained, or may have contained, flammable material.',
              'Use old oil or solvent drums to support any item being welded.',
            ]}
          />
          <Paragraph className="mt-2">
            If in doubt, refer to your Manager or Supervisor. The HSE has specific guidance on
            cleaning and inerting drums and tanks for hot work.
          </Paragraph>
        </Callout>
      </SubSection>

      <SubSection title="Tyres">
        <Paragraph>
          When carrying out welding on a wheel the heat from hot work can generate flammable vapour
          from any oil or flammable residue on the inner rim of the wheel. The rubber itself can
          degrade when heated and release flammable materials. This vapour, confined by the tyre can
          cause an explosion. These explosions are very violent and can kill.
        </Paragraph>
        <DoDoNot
          doItems={[
            "Avoid welding on wheels which have tyres fitted, even if the tyre is deflated. If you can't remove the tyre, use alternative methods.",
          ]}
          doNotItems={['Weld a wheel while the tyre is still fitted to the wheel.']}
        />
      </SubSection>
    </Section>

    <Section number="6" title="Reminder">
      <Paragraph className="font-semibold">
        IF YOU HAVE ANY QUESTIONS PLEASE ASK YOUR MANAGER OR SUPERVISOR. You have a duty not only to
        protect your own health and safety but also to ensure that your actions do not put others
        around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default WeldingDoc;
